import React, { useContext, useState } from 'react';
import { DefaultVersionEnum, EntityTypeEnum, ExportColumnInDTO, ExportFormatEnum, PermissionTypesEnum } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { useExportJob } from '@hooks/useExportJob';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import useNavigation from '@hooks/useNavigation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import ActionMenu, { ActionMenuItem } from '@components/RingilComponents/ActionMenu.tsx';
import { RuleInput, useSecuredDecoration } from '@components/security/securedDecoration';
import { withoutEmptyValues } from './withoutEmptyValues';

export interface TableExportConfig<TFilter> {
  entityType: EntityTypeEnum;
  entityLabel: string;
  /** Optional transform applied to the (already normalized) filter before exporting. */
  mapFilter?: (filter: TFilter) => unknown;
  /**
   * Permission/feature gate for the control. Pass the flag itself rather than dropping `exportConfig`
   * when it isn't granted: the control becomes a secured area, so security mode can highlight it with
   * the clauses that hide it instead of it silently not being there.
   */
  secured?: RuleInput;
}

/**
 * The generic export (GENERIC_EXPORT_API) of a RingilDataTable — v2 and v3 alike: the "Export" link
 * with its CSV / XLSX menu, rendered by the table in its mass-action bar.
 *
 * Exports the filter the table currently shows (normalized, without empty keys). With v3 tabs (`columns`)
 * the menu offers two kinds: the current view, in the open tab's columns, and the full export, in the
 * entity's fixed set (`columns: null`); otherwise just the fixed set. Behind the
 * EXPORT_JOB_CENTER beta, users who can open the jobs center get an async job instead of the legacy
 * banner flow.
 */
export const useTableExport = <TFilter,>({
  exportConfig,
  filterName,
  filter,
  normalizeFilter,
  columns,
  compact = false,
  iconOnly = false,
}: {
  exportConfig?: TableExportConfig<TFilter>;
  /** Table identity; scopes the control's secured-area id. */
  filterName: string;
  filter: TFilter;
  normalizeFilter?: (filter: TFilter) => TFilter;
  /** The current view's columns, read when the export starts; absent = only the entity's fixed (full) export. */
  columns?: () => ExportColumnInDTO[];
  /** A small text button for the v3 bulk-actions bar; the default is v2's link button. */
  compact?: boolean;
  /** With `compact`: the icon alone, its name on hover. */
  iconOnly?: boolean;
}): { exportControl: React.ReactNode; exportShown: boolean } => {
  const { tr } = useTranslationLgs();
  const { activeExports, startExport } = useExportJob();

  // Beta: exports run as async jobs in the jobs center (live progress there, redirect to the job
  // detail on start). Only for users who can actually see the jobs center; others keep the legacy
  // banner flow even when the surface is NEW.
  const { version: exportJobCenterVersion } = useBetaVersion(BetaSurface.EXPORT_JOB_CENTER);
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  const { createExportAsyncJob } = useContext(ApiContext);
  // RW only: download in the jobs center needs PERMISSION_JOBS_RW — routing a READ-only user
  // into the async flow would let them start an export they can never retrieve.
  const exportViaJobCenter = exportJobCenterVersion === DefaultVersionEnum.NEW && hasPermission([PermissionTypesEnum.PERMISSION_JOBS_RW]);

  // Busy state: legacy flow tracks its export context; the jobs-center flow guards its own
  // in-flight create so a double-click cannot start two jobs.
  const [asyncExportStarting, setAsyncExportStarting] = useState(false);
  const anyExportActive = exportViaJobCenter ? asyncExportStarting : Object.values(activeExports).some(exp => exp.uiStatus === 'starting' || exp.uiStatus === 'polling');

  // The gate lives on the config, not on whether the caller passed one: an ungranted export is still
  // an area security mode can highlight, it just renders nothing outside of it.
  const exportDecoration = useSecuredDecoration(exportConfig ? { id: `${filterName}-export`, visible: exportConfig.secured, label: 'Export' } : undefined);
  const exportShown = !!exportConfig && (exportDecoration.visible || exportDecoration.enabled);

  // `exportColumns`: the view's columns; null = the full export (the backend's fixed set); undefined = not sent at all.
  const handleExport = (format: ExportFormatEnum, exportColumns?: ExportColumnInDTO[] | null) => {
    if (!exportConfig || anyExportActive || !exportDecoration.visible) return;
    const baseFilter = withoutEmptyValues(normalizeFilter ? normalizeFilter(filter) : filter);
    const exportFilter = exportConfig.mapFilter ? exportConfig.mapFilter(baseFilter) : baseFilter;
    if (exportViaJobCenter) {
      setAsyncExportStarting(true);
      createExportAsyncJob(
        // Generated type says filter: string, but the API expects the JSON object (same cast as the legacy flow)
        { entityType: exportConfig.entityType, exportFormat: format, filter: exportFilter as never, ...(exportColumns !== undefined ? { columns: exportColumns } : {}) },
        d => {
          setAsyncExportStarting(false);
          nav.navigate(nav.urlFunctions.createJobDetail(d.jobId));
        },
        null,
        {
          successToast: false,
          onConflict: () => {
            setAsyncExportStarting(false);
            showToastMessage(tr('RingilDataTable.exportConflict', 'Export už běží — dokončete nebo zrušte předchozí.'), '', 'warn');
          },
          onError: () => setAsyncExportStarting(false),
        },
      );
      return;
    }
    startExport(exportConfig.entityType, exportConfig.entityLabel, exportFilter, format, exportColumns);
  };

  // Each kind lists its formats under a caption saying what ends up in the file.
  const formatItems = (kind: string, exportColumns?: () => ExportColumnInDTO[] | null, section?: ActionMenuItem['section']): ActionMenuItem[] => [
    {
      key: `${kind}-csv`,
      label: tr('RingilDataTable.exportCsv', 'CSV'),
      icon: <i className="pi pi-file" />,
      onClick: () => handleExport(ExportFormatEnum.CSV, exportColumns?.()),
      section,
      dataTestId: `${filterName}-export-${kind}-csv`,
    },
    {
      key: `${kind}-xlsx`,
      label: tr('RingilDataTable.exportXlsx', 'XLSX'),
      icon: <i className="pi pi-file-excel" />,
      onClick: () => handleExport(ExportFormatEnum.XLSX, exportColumns?.()),
      dataTestId: `${filterName}-export-${kind}-xlsx`,
    },
  ];
  const exportMenuItems = columns
    ? [
        ...formatItems('view', columns, {
          title: tr('RingilDataTable.exportCurrentView', 'Current view'),
          hint: tr('RingilDataTable.exportCurrentViewHint', 'Only the columns of the open tab'),
        }),
        ...formatItems('full', () => null, {
          title: tr('RingilDataTable.exportFull', 'Full export'),
          hint: tr('RingilDataTable.exportFullHint', 'The standard set of columns'),
        }),
      ]
    : formatItems('fixed');

  const exportLabel = tr(`RingilDataTable.export`, `Export`);
  const exportControl = exportConfig
    ? exportDecoration.decorate(
        <span className="inline-flex">
          <ActionMenu
            items={exportMenuItems}
            heading={columns ? undefined : exportLabel}
            icon={<i className="pi pi-download" />}
            mode={compact ? 'text-only' : 'link'}
            density="compact"
            triggerClassName={compact ? (iconOnly ? 'h-6 w-6 p-0 bg-transparent' : 'h-6 px-1 bg-transparent text-xs') : undefined}
            label={compact && iconOnly ? undefined : exportLabel}
            tooltip={compact && iconOnly ? exportLabel : undefined}
            disabled={anyExportActive}
            dataTestId={`${filterName}-export`}
          />
        </span>,
      )
    : null;

  return { exportControl, exportShown };
};
